import type { OrderResponse, OrderStatusResponse, PaymentStatus, RetryPaymentResponse } from '../types/requests'
import type { TwoFactorSetup } from '../types/adminRequests'

/**
 * Runtime-controles op de antwoorden die ertoe doen. TypeScript controleert alleen
 * de code; wat er over het netwerk binnenkomt is `unknown` tot het hier langs is geweest.
 * Voor grote antwoorden (catalogus) is een schema-bibliotheek als zod de volgende stap.
 */

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

/** Alleen https-betaalpagina's: de site stuurt de bezoeker hier blind naartoe. */
export const isSafePaymentUrl = (url: string) => {
  try {
    return new URL(url).protocol === 'https:'
  } catch {
    return false
  }
}

/** Hele, niet-negatieve centen. Een kommagetal betekent dat de server euro's stuurt: factor 100 fout. */
const cents = (v: unknown, name: string): number => {
  if (typeof v !== 'number' || !Number.isSafeInteger(v) || v < 0) throw new Error(`${name} is geen geheel aantal centen`)
  return v
}

const orderId = (v: unknown): string => {
  if (typeof v !== 'string' || !v) throw new Error('orderId ontbreekt')
  return v
}

const gift = (v: unknown): string | null => {
  if (v !== null && typeof v !== 'string') throw new Error('ongeldig cadeau')
  return v
}

const orderNumber = (v: unknown): string | null => {
  if (v !== null && (typeof v !== 'string' || !v)) throw new Error('ongeldig bestelnummer')
  return v
}

const PAYMENT_STATUSES: readonly PaymentStatus[] = ['open', 'betaald', 'mislukt', 'verlopen']

export function parseOrderResponse(raw: unknown): OrderResponse {
  if (!isRecord(raw)) throw new Error('geen object')
  const { paymentUrl } = raw
  if (paymentUrl !== null && (typeof paymentUrl !== 'string' || !isSafePaymentUrl(paymentUrl))) {
    throw new Error('ongeldige betaallink')
  }
  const totalCents = cents(raw.totalCents, 'totalCents')
  const voucherCents = cents(raw.voucherCents, 'voucherCents')
  if (voucherCents > totalCents) throw new Error('cadeaubon groter dan het totaal')
  return {
    orderId: orderId(raw.orderId),
    totalCents,
    voucherCents,
    shippingCents: cents(raw.shippingCents, 'shippingCents'),
    paymentUrl,
    gift: gift(raw.gift),
  }
}

export function parseOrderStatus(raw: unknown): OrderStatusResponse {
  if (!isRecord(raw)) throw new Error('geen object')
  const status = PAYMENT_STATUSES.find((s) => s === raw.status)
  if (!status) throw new Error('onbekende betaalstatus')
  return {
    orderId: orderId(raw.orderId),
    status,
    totalCents: cents(raw.totalCents, 'totalCents'),
    shippingCents: cents(raw.shippingCents, 'shippingCents'),
    gift: gift(raw.gift),
    orderNumber: orderNumber(raw.orderNumber),
    // Optioneel (oude server: ontbreekt). Alleen een echte boolean telt.
    ...(typeof raw.askSource === 'boolean' ? { askSource: raw.askSource } : {}),
  }
}

/** De QR-code wordt als <img> getoond: alleen een data-URL van een afbeelding, nooit een link of HTML. */
export function parseTwoFactorSetup(raw: unknown): TwoFactorSetup {
  if (!isRecord(raw)) throw new Error('geen object')
  const { qrImage, secret } = raw
  if (typeof qrImage !== 'string' || !/^data:image\/(svg\+xml|png)[;,]/.test(qrImage)) throw new Error('ongeldige QR-code')
  if (typeof secret !== 'string' || !/^[A-Z2-7]{16,}$/.test(secret)) throw new Error('ongeldige sleutel')
  return { qrImage, secret }
}

export function parseRetryPayment(raw: unknown): RetryPaymentResponse {
  if (!isRecord(raw) || typeof raw.paymentUrl !== 'string' || !isSafePaymentUrl(raw.paymentUrl)) {
    throw new Error('ongeldige betaallink')
  }
  return { paymentUrl: raw.paymentUrl }
}
